/**
 * Hook pour gérer l'import WordPress
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import { api } from '../../../lib/api'
import type {
  WPContentType,
  WPContentItem,
  WPContentsResponse,
  WPQuota,
  WPEstimate,
  WPImportResponse,
  WPImportStatus,
  ImportState,
  ImportStep,
} from './types'

interface UseWordPressImportOptions {
  onImportComplete?: () => void
  initialCollectionId?: number | null
}

export function useWordPressImport(options: UseWordPressImportOptions = {}) {
  const { onImportComplete, initialCollectionId = null } = options

  // État principal
  const [state, setState] = useState<ImportState>({
    step: 'closed',
    selectedType: null,
    selectedItems: [],
    targetCollectionId: null,
    autoCreateCollections: true,
    taskId: null,
    error: null,
  })

  // Données
  const [contentTypes, setContentTypes] = useState<WPContentType[]>([])
  const [contents, setContents] = useState<WPContentItem[]>([])
  const [quota, setQuota] = useState<WPQuota | null>(null)
  const [estimate, setEstimate] = useState<WPEstimate | null>(null)
  const [importStatus, setImportStatus] = useState<WPImportStatus | null>(null)

  // UI
  const [loading, setLoading] = useState(false)
  const [loadingContents, setLoadingContents] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)

  // Polling ref
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Charger les types de contenus
  const loadContentTypes = useCallback(async () => {
    try {
      setLoading(true)
      const { data } = await api.get<{ content_types: WPContentType[] }>('/wp/content-types')
      setContentTypes(data.content_types)
    } catch (err: any) {
      console.error('Error loading content types:', err)
      setState(s => ({ ...s, error: err.response?.data?.detail || 'Erreur de connexion WordPress' }))
    } finally {
      setLoading(false)
    }
  }, [])

  // Charger le quota
  const loadQuota = useCallback(async () => {
    try {
      const { data } = await api.get<WPQuota>('/wp/quota')
      setQuota(data)
    } catch (err: any) {
      console.error('Error loading quota:', err)
    }
  }, [])

  // Charger les contenus d'un type
  const loadContents = useCallback(async (type: string, pageNum: number = 1, append: boolean = false) => {
    try {
      setLoadingContents(true)
      const { data } = await api.get<WPContentsResponse>('/wp/contents', {
        params: { type, page: pageNum, per_page: 50 }
      })
      
      if (append) {
        setContents(prev => [...prev, ...data.items])
      } else {
        setContents(data.items)
      }
      setTotal(data.total)
      setHasMore(data.has_more)
      setPage(pageNum)
    } catch (err: any) {
      console.error('Error loading contents:', err)
      setState(s => ({ ...s, error: err.response?.data?.detail || 'Erreur chargement contenus' }))
    } finally {
      setLoadingContents(false)
    }
  }, [])

  // Charger plus de contenus
  const loadMore = useCallback(async () => {
    if (!state.selectedType || loadingContents || !hasMore) return
    await loadContents(state.selectedType.type_id, page + 1, true)
  }, [state.selectedType, loadingContents, hasMore, page, loadContents])

  // Estimer l'import
  const loadEstimate = useCallback(async (items: WPContentItem[]) => {
    if (items.length === 0) {
      setEstimate(null)
      return
    }

    try {
      const { data } = await api.post<WPEstimate>('/wp/estimate', {
        content_type: items[0].wp_type,
        wp_ids: items.map(i => i.wp_id),
      })
      setEstimate(data)
    } catch (err: any) {
      console.error('Error estimating import:', err)
    }
  }, [])

  // Ouvrir le drawer
  const open = useCallback(async () => {
    setState(s => ({ 
      ...s, 
      step: 'type_selection', 
      error: null,
      // Pré-sélectionner la collection si fournie
      targetCollectionId: initialCollectionId,
    }))
    await Promise.all([loadContentTypes(), loadQuota()])
  }, [loadContentTypes, loadQuota, initialCollectionId])

  // Fermer le drawer
  const close = useCallback(() => {
    // Arrêter le polling si actif
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }

    setState({
      step: 'closed',
      selectedType: null,
      selectedItems: [],
      targetCollectionId: null,
      autoCreateCollections: true,
      taskId: null,
      error: null,
    })
    setContents([])
    setEstimate(null)
    setImportStatus(null)
  }, [])

  // Sélectionner un type
  const selectType = useCallback(async (type: WPContentType) => {
    setState(s => ({ 
      ...s, 
      step: 'content_selection', 
      selectedType: type,
      selectedItems: [],
    }))
    setContents([])
    setEstimate(null)
    await loadContents(type.type_id, 1)
  }, [loadContents])

  // Toggle sélection item
  const toggleItem = useCallback((item: WPContentItem) => {
    setState(s => {
      const isSelected = s.selectedItems.some(i => i.wp_id === item.wp_id)
      const newItems = isSelected
        ? s.selectedItems.filter(i => i.wp_id !== item.wp_id)
        : [...s.selectedItems, item]
      return { ...s, selectedItems: newItems }
    })
  }, [])

  // Sélectionner tous / aucun
  const selectAll = useCallback(() => {
    setState(s => ({ ...s, selectedItems: [...contents] }))
  }, [contents])

  const selectNone = useCallback(() => {
    setState(s => ({ ...s, selectedItems: [] }))
  }, [])

  // Mettre à jour l'estimation quand la sélection change
  useEffect(() => {
    // Ne charger l'estimation que pendant la sélection des contenus
    if (state.step === 'content_selection' && state.selectedItems.length > 0) {
      const debounce = setTimeout(() => {
        loadEstimate(state.selectedItems)
      }, 300)
      return () => clearTimeout(debounce)
    } else if (state.step === 'content_selection' && state.selectedItems.length === 0) {
      // Reset estimate seulement si on désélectionne tout (pas quand on change d'étape)
      setEstimate(null)
    }
  }, [state.selectedItems, state.step, loadEstimate])

  // Passer à la confirmation
  const goToConfirmation = useCallback(() => {
    if (state.selectedItems.length === 0) return
    setState(s => ({ ...s, step: 'confirmation' }))
  }, [state.selectedItems])

  // Retour à la sélection
  const backToSelection = useCallback(() => {
    setState(s => ({ ...s, step: 'content_selection' }))
  }, [])

  // Retour aux types
  const backToTypes = useCallback(() => {
    setState(s => ({ 
      ...s, 
      step: 'type_selection',
      selectedType: null,
      selectedItems: [],
    }))
    setContents([])
    setEstimate(null)
  }, [])

  // Lancer l'import
  const startImport = useCallback(async () => {
    if (state.selectedItems.length === 0 || !state.selectedType) return

    try {
      setLoading(true)
      
      const { data } = await api.post<WPImportResponse>('/wp/import', {
        content_type: state.selectedType.type_id,
        wp_ids: state.selectedItems.map(i => i.wp_id),
        target_collection_id: state.targetCollectionId,
        auto_create_collections: state.autoCreateCollections,
      })

      setState(s => ({ 
        ...s, 
        step: 'importing',
        taskId: data.task_id,
      }))

      // Démarrer le polling du statut
      startStatusPolling(data.task_id)
    } catch (err: any) {
      console.error('Error starting import:', err)
      setState(s => ({ 
        ...s, 
        step: 'error',
        error: err.response?.data?.detail || 'Erreur lors du lancement de l\'import',
      }))
    } finally {
      setLoading(false)
    }
  }, [state.selectedItems, state.selectedType, state.targetCollectionId, state.autoCreateCollections])

  // Polling du statut
  const startStatusPolling = useCallback((taskId: string) => {
    const poll = async () => {
      try {
        const { data } = await api.get<WPImportStatus>(`/wp/import/${taskId}/status`)
        setImportStatus(data)

        if (data.status === 'completed' || data.status === 'failed' || data.status === 'cancelled') {
          // Arrêter le polling
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current)
            pollIntervalRef.current = null
          }

          if (data.status === 'completed') {
            setState(s => ({ ...s, step: 'completed' }))
            onImportComplete?.()
          } else if (data.status === 'failed') {
            setState(s => ({ 
              ...s, 
              step: 'error',
              error: data.error_message || 'Import échoué',
            }))
          }
        }
      } catch (err) {
        console.error('Error polling status:', err)
      }
    }

    // Premier appel immédiat
    poll()

    // Polling toutes les 2 secondes
    pollIntervalRef.current = setInterval(poll, 2000)
  }, [onImportComplete])

  // Annuler l'import
  const cancelImport = useCallback(async () => {
    if (!state.taskId) return

    try {
      await api.delete(`/wp/import/${state.taskId}`)
      
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }

      close()
    } catch (err: any) {
      console.error('Error cancelling import:', err)
    }
  }, [state.taskId, close])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
      }
    }
  }, [])

  return {
    // État
    state,
    isOpen: state.step !== 'closed',
    
    // Données
    contentTypes,
    contents,
    quota,
    estimate,
    importStatus,
    total,
    hasMore,
    
    // UI
    loading,
    loadingContents,
    
    // Actions
    open,
    close,
    selectType,
    toggleItem,
    selectAll,
    selectNone,
    loadMore,
    goToConfirmation,
    backToSelection,
    backToTypes,
    startImport,
    cancelImport,
    
    // Setters
    setTargetCollection: (id: number | null) => 
      setState(s => ({ ...s, targetCollectionId: id })),
    setAutoCreateCollections: (value: boolean) => 
      setState(s => ({ ...s, autoCreateCollections: value })),
  }
}
